import _ from "lodash";
import React, { Component } from "react";
import { useParams } from "react-router-dom";
import { connect } from "react-redux";
import { FormattedMessage } from "react-intl";
import { scroller, animateScroll as scroll } from "react-scroll";

import { withLayout } from "../../components/layout/Layout";
import { HelpPageSidebar } from "./HelpPageSidebar/HelpPageSidebar";
import { ThreatList } from "./ThreatList/ThreatList";
import { HelpHeader } from "./HelpHeader/HelpHeader";
import { HelpContent } from "./HelpContent";
import { Mobile, NotMobile } from "../../hocs/Responsive";
import { getEulaHeaderHeight, getHeaderHeight } from "../../store/layout";
import { loadHelp, setSearchResult } from "./help.actions";
import { fetchThreats } from "../../store/threats";
import { getHelp } from "./help.reducer";
import { HelpSearch } from "./HelpSearch/HelpSearch";
import { isMobile } from "../../helpers/dom.helper";
import { getOffset } from "../../utils/getOffset";
import { AriaPageTitle } from "../../components/ariaPageTitle";

import "./HelpPage.scss";

const MOBILE_OFFSET = -140;
const NOT_MOBILE_OFFSET = -110;

export class HelpPageComponent extends Component {
  get scrollConfig() {
    const { eulaHeaderHeight } = this.props;
    const basicOffset = isMobile() ? MOBILE_OFFSET : NOT_MOBILE_OFFSET;

    return {
      smooth: true,
      duration: 300,
      offset: basicOffset - eulaHeaderHeight,
    };
  }

  state = {
    searchString: "", // value used for search
    searchInputValue: "", // value in search input
  };

  componentDidMount() {
    const { loadHelp, fetchThreats, isUserAuthenticated, setSearchResult } =
      this.props;

    loadHelp();
    if (isUserAuthenticated) {
      fetchThreats();
    }
    setSearchResult({});
  }

  componentDidUpdate(prevProps, prevState) {
    if (!_.isEqual(prevState.searchString, this.state.searchString)) {
      this.scrollWalker = this.makeScrollWalker();
      this.scrollWalker.next(1);
    }
  }

  handleContentLoad = () => {
    const { section } = this.props;
    if (section) {
      scroller.scrollTo(section, this.scrollConfig);
    }
  };

  handleContentClick = ({ target }) => {
    if (target.nodeName === "A" && _.startsWith(target.hash, "#")) {
      scroller.scrollTo(_.last(_.split(target.hash, "/")), this.scrollConfig);
    }
  };

  render() {
    const {
      help: { sections, error, hasNoMatches },
      section,
      setSearchResult,
    } = this.props;
    // Kludge for rendering ThreatList inside of section
    // so that scrollspy can handle it properly
    (_.find(sections, { id: "threat-list" }) || {}).after =
      this.renderThreatList();

    return (
      <div className="help-page">
        <AriaPageTitle pageKey="page_title_help_page" />
        <NotMobile>
          <aside className="help-page__sidebar">
            <HelpPageSidebar
              sections={sections}
              activeSection={section}
              onSearchChange={this.handleSearchChange}
              onDoSearch={this.handleDoSearch}
              hasNoMatches={hasNoMatches}
              searchValue={this.state.searchInputValue}
            />
          </aside>
        </NotMobile>
        <Mobile>
          <HelpSearch
            hasNoMatches={hasNoMatches}
            onChange={this.handleSearchChange}
            onGoToNext={this.handleGoToNext}
            onGoToPrevious={this.handleGoToPrevious}
            onSearchChange={this.handleSearchChange}
            onDoSearch={this.handleDoSearch}
            value={this.state.searchInputValue}
          />
        </Mobile>
        <article
          className="help-page__content"
          onClick={this.handleContentClick}
          style={{ top: `${this.props.headerHeight}px` }}
        >
          {error
            ? this.renderError(error.key)
            : !_.isEmpty(sections) && (
                <HelpContent
                  className="help-page__content-section"
                  sections={sections}
                  searchString={this.state.searchString}
                  onHighlightDone={setSearchResult}
                  onLoad={this.handleContentLoad}
                />
              )}
        </article>
      </div>
    );
  }

  renderThreatList() {
    const { threats, useFallbackLanguage, isUserAuthenticated } = this.props;

    if (!isUserAuthenticated) {
      return this.renderErrorWithLink(
        "threat_list_is_not_visible",
        "link_to_login_page"
      );
    }

    if (_.isEmpty(threats)) {
      return this.renderError("cannot_load_threat_list");
    }

    return (
      <ThreatList threats={threats} useFallbackLanguage={useFallbackLanguage} />
    );
  }

  renderError(key) {
    return (
      <div className="help-page__error help-section">
        <FormattedMessage id={key} />
      </div>
    );
  }

  renderErrorWithLink(error, link) {
    const { loginLink } = this.props;

    return (
      <div className="help-section">
        <div className="help-page__error ">
          <FormattedMessage id={error} />
        </div>
        <a href={loginLink} className="link">
          <FormattedMessage id={link} />
        </a>
      </div>
    );
  }

  handleDoSearch = (searchString) => {
    this.setState({ searchString });
  };

  handleSearchChange = (searchInputValue) => {
    this.setState({ searchInputValue });
  };

  handleGoToNext = () => {
    this.scrollWalker.next(1);
  };

  handleGoToPrevious = () => {
    this.scrollWalker.next(-1);
  };

  *makeScrollWalker() {
    const allResults = document.querySelectorAll("mark");
    const resultsLength = allResults.length;

    if (resultsLength === 0) {
      return;
    }

    const offsetResults = [...allResults].map(getOffset);

    let currentIndex = 0;
    while (true) {
      const offsetTop =
        offsetResults[currentIndex].top + this.scrollConfig.offset - 20;
      scroll.scrollTo(offsetTop, this.scrollConfig);

      const offset = yield;
      currentIndex =
        (((currentIndex + offset) % resultsLength) + resultsLength) %
        resultsLength;
    }
  }
}

const HelpPageRouterProvider = ({ ...props }) => {
  const { section } = useParams();

  return <HelpPageComponent {...props} section={section} />;
};

export const HelpPage = withLayout({
  customHeader: () => <HelpHeader />,
  showCustomHeader: true,
  showNavigation: false,
  showUserInfo: false,
})(
  connect(
    (state) => ({
      help: getHelp(state),
      threats: _.orderBy(state.threats.list, "name"),
      useFallbackLanguage: state.config.useFallbackLanguage,
      isUserAuthenticated: _.has(state.subscriberInfo, "id"),
      loginLink: state.config.loginLink,
      eulaHeaderHeight: getEulaHeaderHeight(state),
      headerHeight: getHeaderHeight(state),
    }),
    {
      loadHelp,
      fetchThreats,
      setSearchResult,
    }
  )(HelpPageRouterProvider)
);
